import { Component, EventEmitter, Input, Output } from '@angular/core';

import { FormControl, FormGroup, FormsModule } from '@angular/forms';
import { MenuModule } from 'primeng/menu';
import { RippleModule } from 'primeng/ripple';
import { ButtonModule } from 'primeng/button';
import {
  IBenefits,
  ItemBenefitsComponent,
} from '../product/item-benefits/item-benefits.component';
import { ItemPaymentTotalsComponent } from './item-payment-totals/item-payment-totals.component';
import { CouponCodeInputComponent } from '../coupon-code-input/coupon-code-input.component';
import { PaymentMethodsLogoListComponent } from '../payment-methods-logo-list/payment-methods-logo-list.component';
import { Router, RouterLink } from '@angular/router';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  TermsDialogComponent,
  TermsDialogData,
} from '../../dialogs/terms-dialog/terms-dialog.component';
import { Customer, Order, RegisterMutation, RegisterMutationVariables } from '../../../graphql/generated/generated-types';
import { FormatPricePipe } from '../../pipes/format-price.pipe';
import { CommonModule } from '@angular/common';
import { CheckoutDeliveryType } from '../../../routes/checkout/form.functions';
import { CheckoutField } from '../../../routes/checkout/fields.enum';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AccountBenefitsComponent } from '../account-benefits/account-benefits.component';
import { DataService } from '../../../core/providers/data/data.service';
import { REGISTER } from '../../dialogs/auth/register-dialog/register.graphql';
import { take } from 'rxjs';
import { AuthErrorDialogComponent } from '../../dialogs/auth/auth-error-dialog/auth-error-dialog.component';
import { AuthSuccessDialogBody, AuthSuccessDialogComponent, AuthSuccessDialogType } from '../../dialogs/auth/auth-success-dialog/auth-success-dialog.component';

@Component({
  selector: 'app-payment-summary-block',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MenuModule,
    RippleModule,
    ButtonModule,
    ItemBenefitsComponent,
    ItemPaymentTotalsComponent,
    CouponCodeInputComponent,
    PaymentMethodsLogoListComponent,
    RouterLink,
    TermsDialogComponent,
    FormatPricePipe,
    ProgressSpinnerModule,
    AccountBenefitsComponent,
    AuthErrorDialogComponent,
    AuthSuccessDialogComponent
  ],
  templateUrl: './payment-summary-block.component.html',
  styleUrl: './payment-summary-block.component.scss',
})
export class PaymentSummaryBlockComponent {
  @Input() order: Order;
  @Input() deliveryOptionsControl: FormControl;
  @Input() courierDeliveryControl: FormControl;
  @Input() form!: FormGroup;
  @Input() benefitsEnabled: boolean = true;
  @Input() stickyToTop: boolean = false;
  @Input() inCart!: boolean;
  @Input() inCheckout!: boolean;
  @Input() inOrderConfirmation!: boolean;
  @Input() loading!: boolean;
  @Input() userLoggedIn?: boolean;
  @Output() proceedToPayment: EventEmitter<any> = new EventEmitter<any>();
  public cartBenefits: IBenefits = {
    returnWarranityDays: 14,
  };

  public CheckoutDeliveryType = CheckoutDeliveryType;
  public CheckoutField = CheckoutField;
  constructor(private dialogService: DialogService, private router: Router, private dataService: DataService) { }

  public goToCheckout() {
    this.router.navigate(['/checkout']);
  }

  public showApproveDialog() {
    const dialogRef: DynamicDialogRef = this.dialogService.open(
      TermsDialogComponent,
      {
        data: {
          cancelBtnText: 'Cancel',
          approveBtnText: 'AGREE AND PAY',
        } as TermsDialogData,
        modal: true,
        dismissableMask: true,
        styleClass: 'c-dialog medium-dialog',
        header: 'Agree our terms and conditions',
      }
    );

    dialogRef.onClose.subscribe((result) => {
      if (result) {
        this.proceedToPayment.emit();
      }
    });
  }

  public register() {
    this.dataService.mutate<RegisterMutation, RegisterMutationVariables>(REGISTER, {
      input: {
        firstName: this.order.customer?.firstName,
        lastName: this.order.customer?.lastName,
        emailAddress: this.order.customer?.emailAddress as any,
      },
    })
      .pipe(take(1))
      .subscribe({
        next: ({ registerCustomerAccount }) => {
          switch (registerCustomerAccount.__typename) {
            case 'Success':
              break;
          }
        },
        error: () => {
          this.openAuthErrorDialog();
        },
        complete: () => {
          this.openAuthSuccessDialog();
        },
      });
  }

  public openAuthSuccessDialog() {

    const dialogRef: DynamicDialogRef = this.dialogService.open(
      AuthSuccessDialogComponent,
      {
        styleClass: 'auth-custom-dialog',
        modal: true,
        showHeader: false,
        closeOnEscape: true,
        dismissableMask: true,
        data: {
          email: this.order.customer?.emailAddress,
          type: 'Register' as AuthSuccessDialogType,
        } as AuthSuccessDialogBody
      }
    );

    dialogRef.onClose.subscribe((result) => { });
  }


  public openAuthErrorDialog() {
    const dialogRef: DynamicDialogRef = this.dialogService.open(
      AuthErrorDialogComponent,
      {
        styleClass: 'auth-custom-dialog',
        modal: true,
        showHeader: false,
        closeOnEscape: true,
        dismissableMask: true,
      }
    );

    dialogRef.onClose.subscribe((result) => { });
  }

  public checkValidation() {
    this.form.markAllAsTouched();
    const contactsControl = this.form.get(CheckoutField.contacts);
    const kenitalaControl = contactsControl?.get(CheckoutField.customFields)?.get(CheckoutField.kenitala);
    const kenitalaValue = kenitalaControl?.value;
    const isKenitalaValid = kenitalaValue?.length === 10 && /^\d+$/.test(kenitalaValue);
    const isLegalPerson = contactsControl?.get(CheckoutField.legalPerson)?.value === true;

    const isFormValid = this.form?.valid;
    const isCourierDeliveryValid = this.courierDeliveryControl?.value;
    const isContactsValid = contactsControl?.valid;

    if (isLegalPerson) {
      if (this.deliveryOptionsControl?.value === CheckoutDeliveryType.Courier) {
        return isCourierDeliveryValid && isFormValid && isKenitalaValid;
      } else {
        return isContactsValid && isKenitalaValid;
      }
    } else {
      if (kenitalaValue && !isKenitalaValid) {
        return false; // Invalid kenitala for non-legal person
      }

      if (this.deliveryOptionsControl?.value === CheckoutDeliveryType.Courier) {
        return isCourierDeliveryValid && isFormValid;
      } else {
        return isContactsValid;
      }
    }


  }

}
